import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { MdEmail, MdWork, MdLocationOn, MdApartment, MdPhone } from 'react-icons/md';
import { EmployeeAvatar } from 'components/common/select/EmployeeBadge';

const CARD_WIDTH = 264;
const CLOSE_DELAY = 120;

/**
 * Wraps any trigger so hovering it reveals a person's identity card — avatar,
 * full name, email, title, site, department, phone.
 *
 * Rendered through a portal because the screens using it (employee detail panels,
 * booking detail cards) clip their content with `overflow-hidden`; the position is
 * taken from the trigger's viewport rect so it stays put inside scroll containers.
 *
 * `person` accepts either shape used across the app — the employee payload
 * (`full_name`, `email`, `title`, `site`, …) or the travel booking's traveller
 * entry (`employee_full_name`, `employee_email`, …) — so callers pass their row
 * through untouched.
 */
const PersonHoverCard = ({ person, children, className = '' }) => {
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const closeTimer = useRef(null);

  const name     = person?.name || person?.employee_name || '';
  const fullName = person?.full_name || person?.employee_full_name || '';
  const email    = person?.email || person?.employee_email || '';
  const phone    = person?.phone_number || person?.employee_phone || '';

  const open = useCallback(() => {
    clearTimeout(closeTimer.current);
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Flip left / above when the card would spill out of the viewport.
    const left = Math.min(rect.left, window.innerWidth - CARD_WIDTH - 12);
    const openUp = rect.bottom + 190 > window.innerHeight && rect.top > 200;
    setPos({
      top: openUp ? undefined : rect.bottom + 6,
      bottom: openUp ? window.innerHeight - rect.top + 6 : undefined,
      left: Math.max(12, left),
    });
  }, []);

  // Small delay so moving the pointer onto the card itself does not close it.
  const scheduleClose = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setPos(null), CLOSE_DELAY);
  }, []);

  const rows = [
    email && { Icon: MdEmail, value: email },
    person?.title && { Icon: MdWork, value: person.title },
    person?.site && { Icon: MdLocationOn, value: person.site },
    person?.department && { Icon: MdApartment, value: person.department },
    phone && { Icon: MdPhone, value: phone },
  ].filter(Boolean);

  if (!person) return children;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={open}
        onMouseLeave={scheduleClose}
        onFocus={open}
        onBlur={scheduleClose}
        // cursor-pointer on the trigger: the name reacts to the pointer, so it
        // must not keep the text I-beam. Set here rather than per call site so
        // every place that shows a person card behaves the same.
        className={`tw-inline-flex tw-cursor-pointer ${className}`}
      >
        {children}
      </span>

      {pos && createPortal(
        <div
          role="tooltip"
          onMouseEnter={() => clearTimeout(closeTimer.current)}
          onMouseLeave={scheduleClose}
          style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, width: CARD_WIDTH, zIndex: 100002 }}
          className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-xl tw-p-3"
        >
          <div className="tw-flex tw-items-center tw-gap-2.5 tw-mb-2">
            <EmployeeAvatar name={name} size={38} fontSize={13} />
            <div className="tw-min-w-0 tw-flex-1">
              <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-slate-800 tw-truncate" title={fullName || name}>
                {fullName || name}
              </p>
              {name && fullName && (
                <p className="tw-m-0 tw-text-xs tw-text-slate-400 tw-truncate">{name}</p>
              )}
            </div>
            {person.active === false && (
              <span className="tw-text-[10px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-bg-red-50 tw-text-red-600 tw-shrink-0">
                inactive
              </span>
            )}
          </div>

          {rows.length > 0 ? (
            <div className="tw-flex tw-flex-col tw-gap-1 tw-border-t tw-border-slate-100 tw-pt-2">
              {rows.map(({ Icon, value }) => (
                <p key={value} className="tw-m-0 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-slate-600 tw-min-w-0">
                  <Icon size={12} className="tw-text-slate-400 tw-shrink-0" />
                  <span className="tw-truncate" title={value}>{value}</span>
                </p>
              ))}
            </div>
          ) : (
            <p className="tw-m-0 tw-text-xs tw-text-slate-400 tw-border-t tw-border-slate-100 tw-pt-2">
              No further details recorded.
            </p>
          )}
        </div>,
        document.body,
      )}
    </>
  );
};

PersonHoverCard.propTypes = {
  person:    PropTypes.object,
  children:  PropTypes.node.isRequired,
  className: PropTypes.string,
};

export default PersonHoverCard;
